import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useNotifier } from '@provider/notifier';
import { makeTanstackQuery } from '@provider/tanstack';
import { useUserIdentityState } from '@provider/user-identity';
import { DAYS_TO_MS, HOURS_TO_MS } from '@utils/constants';
import { getUserPreferences } from './request.get-user-preferences.js';
import { setUserPreferences } from './request.set-user-preferences.js';

const defaultUserPreferences = {
  speechSynthesis: {
    speed: 2,
    gender: "female"
  },
  font: "default"
};
const userPreferencesQuery = makeTanstackQuery({
  defaultQueryConfig: {
    staleTime: 8 * HOURS_TO_MS,
    gcTime: 7 * DAYS_TO_MS,
    meta: {
      persist: true,
      revalidateOnRefresh: true
    }
  },
  queryKey: (input) => [
    "userPreferences",
    input.userId
  ],
  queryFn: async (input) => {
    if (input.userId === null) {
      return null;
    }
    return await getUserPreferences(input.userId);
  },
  useQueryInput(queryFactory) {
    const user = useUserIdentityState((state) => state.user);
    return queryFactory({ userId: (user == null ? void 0 : user.id) ?? null });
  }
});
function useUserPreferences() {
  return useQuery(userPreferencesQuery.useQueryConfig()).data ?? defaultUserPreferences;
}
function useUserPreferencesMutation() {
  const client = useQueryClient();
  const notifier = useNotifier();
  const user = useUserIdentityState((state) => state.user);
  return useMutation(userPreferencesMutation(client, notifier, user));
}
const userPreferencesMutation = (client, notifier, user) => {
  return {
    mutationKey: ["user-preferences", user == null ? void 0 : user.id],
    mutationFn: async (input) => {
      if (!user) {
        throw new Error(
          "userPreferencesMutation(): can only update user preferences for signed in users"
        );
      }
      const objectId = await setUserPreferences(user, input);
      return {
        ...input,
        objectId
      };
    },
    onMutate: async (input) => {
      const queryKey = userPreferencesQuery.queryKey({
        userId: (user == null ? void 0 : user.id) ?? null
      });
      await client.cancelQueries({ queryKey });
      const prevValue = client.getQueryData(queryKey);
      client.setQueryData(queryKey, input);
      client.setQueryData(
        userPreferencesQuery.queryKey({
          userId: null
        }),
        input
      );
      return { prevValue };
    },
    onSuccess: (result) => {
      const queryKey = userPreferencesQuery.queryKey({
        userId: (user == null ? void 0 : user.id) ?? null
      });
      client.setQueryData(queryKey, result);
    },
    onError: (error, input, context) => {
      notifier.notify({
        id: "user-preferences-mutation.error",
        type: "error",
        body: "Vi kunne ikke gemme dine indstillinger. Pr\xF8v igen om lidt."
      });
      const queryKey = userPreferencesQuery.queryKey({
        userId: (user == null ? void 0 : user.id) ?? null
      });
      client.setQueryData(queryKey, context == null ? void 0 : context.prevValue);
    }
  };
};

export { defaultUserPreferences, useUserPreferences, useUserPreferencesMutation, userPreferencesQuery };
//# sourceMappingURL=query.get-user-preferences.js.map
